<template>
  <div>
    <!-- CSVインポート設定フォーム -->
    <div class="row mb-3">
      <div class="col-2 mb-2">
        <p class="text-center py-1 fs-6">マスタ項目</p>
      </div>
      <div class="col-3 mb-2">
        <div class="input-group">
          <bs-select
            :options="csvImportOptions"
            :modelValue="selectedMasterType"
            @update:modelValue="$emit('update:selectedMasterType', $event)"
            :null-option="true"
            :classParam="'form-select'"
          ></bs-select>
        </div>
      </div>
      <div class="col-2 mb-2">
        <p class="text-center py-1 fs-6">CSVファイル</p>
      </div>
      <div class="col-3 mb-2">
        <input
          type="file"
          class="form-control"
          accept=".csv"
          @change="$emit('file-upload', $event)"
        />
      </div>
    </div>

    <!-- アクションボタン -->
    <div class="row">
      <div class="col text-center"></div>
      <div class="col text-center">
        <button 
          class="btn btn-primary" 
          :disabled="!selectedMasterType || !hasFileSelected"
          @click="$emit('preview')"
        >
          プレビュー
        </button>
      </div>
      <div class="col text-end">
        <button 
          class="btn btn-light btn-outline-secondary" 
          @click="$emit('clear')"
        >
          入力クリア
        </button>
      </div>
    </div>

    <!-- プレビューテーブル -->
    <div v-if="previewData.length > 0" class="table-responsive mt-5">
      <div class="row mb-3">
        <div class="col">
          <h4>インポート内容確認</h4>
        </div>
        <div class="col text-end">
          <button
            class="btn btn-primary"
            :disabled="!canImport"
            @click="$emit('import')"
          >
            登録
          </button>
        </div>
      </div>

      <table class="table table-striped">
        <thead>
          <tr>
            <th v-for="column in getPreviewColumns()" :key="column.key">
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in paginatedData" :key="index">
            <td v-for="column in getPreviewColumns()" :key="column.key">
              {{ item[column.key] }}
            </td>
          </tr>
        </tbody>
      </table>

      <!-- ページネーション -->
      <nav v-if="totalPages > 1">
        <ul class="pagination">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a 
              class="page-link" 
              href="#"
              @click.prevent="$emit('page-change', currentPage - 1)"
            >前</a>
          </li>
          <li 
            class="page-item" 
            v-for="page in pagesToShow" 
            :key="page"
            :class="{ active: currentPage === page }"
          >
            <a 
              class="page-link" 
              href="#"
              @click.prevent="$emit('page-change', page)"
            >{{ page }}</a>
          </li>
          <li
            class="page-item"
            :class="{ disabled: currentPage === totalPages }"
          >
            <a 
              class="page-link" 
              href="#"
              @click.prevent="$emit('page-change', currentPage + 1)"
            >次</a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
import BsSelect from '../../../atoms/BsSelect.vue'

export default {
  name: 'CsvImportForm',
  components: {
    BsSelect
  },
  props: {
    csvImportOptions: {
      type: Array,
      required: true
    },
    selectedMasterType: {
      type: String,
      default: ''
    },
    previewData: {
      type: Array,
      default: () => []
    },
    canImport: {
      type: Boolean,
      default: false
    },
    totalPages: {
      type: Number,
      default: 1
    },
    currentPage: {
      type: Number,
      default: 1
    },
    pagesToShow: {
      type: Array,
      default: () => [1]
    },
    hasFileSelected: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'update:selectedMasterType',
    'file-upload',
    'preview',
    'import',
    'clear',
    'page-change'
  ],
  computed: {
    paginatedData() {
      const itemsPerPage = 50
      const start = (this.currentPage - 1) * itemsPerPage
      const end = start + itemsPerPage
      return this.previewData.slice(start, end)
    }
  },
  methods: {
    getPreviewColumns() {
      // CSVインポート用マスタオプションからvalueを取得
      const selectedMasterValue = this.csvImportOptions.find(
        type => type.label === this.selectedMasterType
      )?.value

      // 基本的なカラムマッピング
      const columnMap = {
        event: [
          { key: 'event_cd', label: '種目コード' },
          { key: 'event_name', label: '種目名' },
          { key: 'event_short_name', label: '種目略称' },
          { key: 'event_view_number', label: '表示番号' }
        ],
        race_type: [
          { key: 'race_type_cd', label: '格式コード' },
          { key: 'race_type_name', label: '格式名' },
          { key: 'race_numer', label: '格式番号' },
          { key: 'point_flg', label: 'ポイントフラグ' }
        ],
        event_race_type: [
          { key: 'event_cd', label: '種目コード' },
          { key: 'race_type_cd', label: '格式コード' },
          { key: 'race_type_name', label: '格式名' },
          { key: 'race_sports_year', label: 'スポーツ年度' }
        ],
        race_owner: [
          { key: 'race_owner_login_id', label: '主催者コード' },
          { key: 'race_owner_name', label: '主催者名' },
          { key: 'race_owner_name_kana', label: '主催者名カナ' }
        ],
        race_owner_event: [
          { key: 'race_owner_id', label: '主催者ID' },
          { key: 'event_cd', label: '種目コード' }
        ],
        race_owner_access: [
          { key: 'race_owner_id', label: '主催者ID' },
          { key: 'race_owner_access_cd', label: 'アクセスコード' },
          { key: 'race_owner_access_name', label: 'アクセス名' }
        ],
        class_owner: [
          { key: 'class_owner_kbn_cd', label: '種別区分コード' },
          { key: 'class_owner_kbn_name', label: '種別区分名' },
          { key: 'class_owner_cd', label: '種別コード' },
          { key: 'class_owner_name', label: '種別名' }
        ],
        hall: [
          { key: 'mfj_hall_hall_cd', label: '会場コード' },
          { key: 'mfj_hall_hall_name', label: '会場名' },
          { key: 'mfj_hall_hall_kana', label: '会場名カナ' },
          { key: 'mfj_hall_circuit', label: 'サーキット名' }
        ],
        race_series: [
          { key: 'event_cd', label: '種目コード' },
          { key: 'race_type_cd', label: '格式コード' },
          { key: 'race_series_cd', label: 'シリーズコード' },
          { key: 'race_series_name', label: 'シリーズ名' },
          { key: 'view_number', label: '表示番号' }
        ],
        displace: [
          { key: 'event_cd', label: '種目コード' },
          { key: 'displace_cd', label: '排気量コード' },
          { key: 'displace_name', label: '排気量名' },
          { key: 'displace_short_name', label: '排気量略称' },
          { key: 'view_number', label: '表示番号' }
        ]
      }

      return columnMap[selectedMasterValue] || []
    }
  }
}
</script>

<style scoped>
.btn-light.btn-outline-secondary {
  color: #6c757d;
  border-color: #6c757d;
}
.btn-light.btn-outline-secondary:hover {
  color: #fff;
  background-color: #6c757d;
  border-color: #6c757d;
}
.table th {
  background-color: #f8f9fa;
  font-weight: 600;
}
.pagination {
  justify-content: center;
  margin-top: 1rem;
}
h4 {
  color: #495057;
  font-weight: 600;
}
</style>